import { useCallback, useEffect, useState } from "react";

import { ValueSetExpansionContains } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Combobox, Pill, PillsInput, TextInput, TextInputProps, useCombobox } from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";

import { IconSearch } from "@tabler/icons-react";
import { isEmpty } from "lodash";

import { getValueSetOptions } from "@/utils/medplum";

export interface ValueSetSearchInputProps
  extends Omit<TextInputProps, "onChange" | "defaultValue" | "value"> {
  binding: string;
  onChange: (value: ValueSetExpansionContains | undefined) => void;
  onRemove?: (selectedOption: ValueSetExpansionContains) => void;
  onMapOptions?: (option: ValueSetExpansionContains) => ValueSetExpansionContains;
  minInputLength?: number;
  defaultValue?: ValueSetExpansionContains;
  value?: ValueSetExpansionContains | null;
  preload?: boolean;
  creatable?: boolean;
}

interface ValueSetOption {
  value: string;
  label: string;
}

const VALUE_SET_RESULTS_PAGE_SIZE = 10;
const DROPDOWN_MAX_HEIGHT = 400;
const CUSTOM_ITEM_VALUE = "_custom";

function codingIdentity(coding: ValueSetExpansionContains | null | undefined): string {
  if (!coding) return "";
  return `${coding.system ?? ""}|${coding.code ?? ""}|${coding.display ?? ""}`;
}

const ValueSetSearchInput = (props: ValueSetSearchInputProps) => {
  const {
    binding,
    onRemove,
    onChange,
    creatable,
    defaultValue,
    value,
    minInputLength,
    preload,
    onMapOptions,
    ...inputProps
  } = props;
  const isControlled = Object.prototype.hasOwnProperty.call(props, "value");
  const initialValue = (isControlled ? value : defaultValue) ?? undefined;

  const medplum = useMedplum();
  const [search, setSearch] = useState(initialValue?.display ?? "");
  const [selectedOption, setSelectedOption] = useState<ValueSetExpansionContains | undefined>(
    initialValue,
  );
  const [codingValue, setCodingValue] = useState<ValueSetExpansionContains[]>();
  const [valueSetOptions, setValueSetOptions] = useState<ValueSetOption[]>([]);
  const [isLoading, { open: startLoading, close: stopLoading }] = useDisclosure(false);
  const debouncedSearch = useDebouncedValue(search, 300)[0];

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  useEffect(() => {
    if (!isControlled) return;

    const nextValue = value ?? undefined;
    setSelectedOption((previous) =>
      codingIdentity(previous) === codingIdentity(nextValue) ? previous : nextValue,
    );
    setSearch(nextValue?.display ?? "");
  }, [isControlled, value]);

  useEffect(() => {
    const fetchValueSet = async () => {
      startLoading();
      try {
        const filter = debouncedSearch?.trim() || undefined;
        let valueSetResults = await getValueSetOptions(medplum, binding, {
          ...(filter !== undefined && { filter }),
          count: VALUE_SET_RESULTS_PAGE_SIZE,
        });
        if (onMapOptions) valueSetResults = valueSetResults?.map(onMapOptions);
        setCodingValue(valueSetResults);

        const options = (valueSetResults
          ?.map((option) => {
            return {
              value: option.code,
              label: option.display,
            };
          })
          ?.filter((option) => !isEmpty(option.value)) ?? []) as ValueSetOption[];

        setValueSetOptions(options);
      } finally {
        stopLoading();
      }
    };
    if (debouncedSearch || preload) fetchValueSet();
  }, [medplum, binding, debouncedSearch, startLoading, stopLoading, preload, onMapOptions]);

  const handleRemoveSelectedOption = useCallback(() => {
    setSelectedOption(undefined);
    setSearch("");
    if (selectedOption) onRemove?.(selectedOption);
    onChange(undefined);
  }, [onRemove, onChange, selectedOption]);

  const handleOptionSubmit = useCallback(
    (optionValue: string) => {
      try {
        let selectedCoding;
        if (optionValue === CUSTOM_ITEM_VALUE) {
          const trimmedSearch = (search ?? "").trim();
          selectedCoding = {
            system: "",
            code: trimmedSearch,
            display: trimmedSearch,
          };
        } else {
          selectedCoding = codingValue?.find((item) => item.code === optionValue);
        }

        onChange(selectedCoding);
        setSelectedOption(selectedCoding);
      } finally {
        setSearch("");
        combobox.closeDropdown();
      }
    },
    [search, onChange, codingValue, combobox],
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value: nextSearch } = event.currentTarget;

      setSearch(nextSearch);
      if (minInputLength && nextSearch.length < minInputLength) return;

      combobox.openDropdown();
      combobox.updateSelectedOptionIndex();
    },
    [minInputLength, combobox],
  );

  const { onFocus } = inputProps;
  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      combobox.openDropdown();
      onFocus?.(event);
    },
    [onFocus, combobox],
  );

  const hasDisplayableSelection = Boolean(selectedOption?.display?.trim());

  // Filter out the currently selected option from dropdown options
  const filteredOptions = valueSetOptions.filter(
    (option) => !selectedOption?.code || option.value !== selectedOption.code,
  );

  const showCreateOption =
    !isLoading && !!debouncedSearch && creatable && (search?.trim()?.length ?? 0) > 0;
  const showOptions = !isLoading && filteredOptions.length > 0;
  const showNoResultsFound =
    !creatable && !isLoading && !!debouncedSearch && filteredOptions.length === 0;
  const shouldHideDropdown = !showCreateOption && !showOptions && !showNoResultsFound && !isLoading;
  const hideDropdown = shouldHideDropdown && !combobox.dropdownOpened;

  return (
    <Combobox shadow="sm" store={combobox} onOptionSubmit={handleOptionSubmit}>
      <Combobox.Target>
        {hasDisplayableSelection ? (
          <PillsInput
            rightSection={<IconSearch size={18} />}
            {...inputProps}
            miw={0}
            w="100%"
            styles={{
              input: { overflow: "hidden" },
            }}
          >
            <Pill
              maw="100%"
              onRemove={handleRemoveSelectedOption}
              withRemoveButton={!inputProps.disabled}
              removeButtonProps={{ "aria-label": "Clear search" }}
              styles={{
                root: { maxWidth: "100%", overflow: "hidden" },
                label: {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            >
              {selectedOption!.display}
            </Pill>
          </PillsInput>
        ) : (
          <TextInput
            {...inputProps}
            miw={0}
            w="100%"
            value={search}
            onFocus={handleFocus}
            onChange={handleChange}
          />
        )}
      </Combobox.Target>
      <Combobox.Dropdown
        hidden={hideDropdown}
        mah={DROPDOWN_MAX_HEIGHT}
        style={{ overflowY: "auto" }}
      >
        <Combobox.Options>
          <Combobox.Option
            disabled
            value=""
            fw={600}
            style={{ cursor: "default", color: "var(--mantine-color-orange-6)", opacity: 1 }}
          >
            Not seeing your answer? Search by keyword.
          </Combobox.Option>
          {isLoading && <Combobox.Empty>Searching...</Combobox.Empty>}
          {showOptions &&
            filteredOptions.map((option) => (
              <Combobox.Option key={option.value} value={option.value}>
                {option.label}
              </Combobox.Option>
            ))}
          {showCreateOption && (
            <Combobox.Option value={CUSTOM_ITEM_VALUE}>+ Create "{search?.trim()}"</Combobox.Option>
          )}
          {showNoResultsFound && <Combobox.Empty>No results found</Combobox.Empty>}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export default ValueSetSearchInput;
